/**
 * In-memory SheetsClient — used by dev-mode to drive the app without
 * touching real Google Sheets. Implements the same `SheetsClient`
 * interface so the rest of the app is unchanged.
 *
 * Range syntax supported: `tab!A1:Z` (the only shape Mochia generates).
 * Anything weirder (named ranges, sheet ids) will throw — fine, we
 * control all callers.
 */

import type {
  BatchGetResponse,
  BatchUpdateRequest,
  DriveFilesListResponse,
  SheetsClient,
  SpreadsheetCreateProps,
  SpreadsheetCreateResponse,
  ValueRange,
} from '@/lib/sheets/client';
import { SPREADSHEET_NAME, TABS, type TabName } from '@/lib/sheets/schema';
import { DEV_SPREADSHEET_ID } from './index';

interface Tab {
  name: string;
  rows: string[][];
}

interface Workbook {
  id: string;
  title: string;
  tabs: Map<string, Tab>;
}

let store: Workbook | null = null;

function ensureStore(): Workbook {
  if (store) return store;
  store = createSeededWorkbook();
  return store;
}

/** Reset all in-memory state — exposed for tests / dev console. */
export function __resetMemoryStore(): void {
  store = null;
}

function createSeededWorkbook(): Workbook {
  const now = '2026-05-03T00:00:00Z';
  const tabs = new Map<string, Tab>();

  for (const tabName of Object.keys(TABS) as TabName[]) {
    const headers = TABS[tabName].headers;
    tabs.set(tabName, {
      name: tabName,
      rows: [[...headers]],
    });
  }

  // ─── Books: 家庭 (default) + 專案 ───
  pushRow(tabs, 'books', {
    id: 'book-family',
    name: '家庭',
    color_token: '--terracotta-500',
    is_default: 'true',
    rev: '1',
    created_at: now,
    updated_at: now,
    deleted_at: '',
    type: 'family',
  });
  pushRow(tabs, 'books', {
    id: 'book-project-template',
    name: '專案',
    color_token: '--matcha-700',
    is_default: 'false',
    rev: '1',
    created_at: now,
    updated_at: now,
    deleted_at: '',
    type: 'project',
  });

  // ─── Seed categories (system-wide, both books) ───
  // All icon names below MUST exist in src/components/common/Icon.tsx
  // IconName union — otherwise the AddTxScreen sub-cat picker crashes.
  const seedCats: Array<[string, string, string, string, string, string]> = [
    // [id, name, kind, color_token, icon, parent_id]
    ['cat-food', '飲食', 'expense', '--cat-food', 'utensils', ''],
    ['sub-breakfast', '早餐', 'expense', '--cat-food', 'utensils', 'cat-food'],
    ['sub-lunch', '午餐', 'expense', '--cat-food', 'utensils', 'cat-food'],
    ['sub-dinner', '晚餐', 'expense', '--cat-food', 'utensils', 'cat-food'],
    ['cat-transit', '交通', 'expense', '--cat-transit', 'car', ''],
    ['sub-mrt', '捷運', 'expense', '--cat-transit', 'car', 'cat-transit'],
    ['cat-home', '居家', 'expense', '--cat-home', 'house', ''],
    ['cat-fun', '娛樂', 'expense', '--cat-fun', 'gamepad', ''],
    ['cat-salary', '薪資', 'income', '--cat-salary', 'briefcase', ''],
    ['sub-monthly-salary', '月薪', 'income', '--cat-salary', 'wallet', 'cat-salary'],
  ];
  for (let i = 0; i < seedCats.length; i++) {
    const [id, name, kind, color_token, icon, parent_id] = seedCats[i]!;
    pushRow(tabs, 'categories', {
      id,
      parent_id,
      name,
      kind,
      color_token,
      icon,
      sort_order: String(i),
      is_system: 'true',
      rev: '1',
      created_at: now,
      updated_at: now,
      deleted_at: '',
      book_id: '', // empty = available to all books
    });
  }

  // ─── Sample transactions (mix of TWD + JPY for FX testing) ───
  const sampleTxs: Array<Record<string, string>> = [
    {
      id: 'tx-salary-1',
      book_id: 'book-family',
      date: '2026-05-05',
      amount_minor: '12500000', // 125,000 TWD
      currency: 'TWD',
      kind: 'income',
      category_id: 'cat-salary',
      subcategory_id: 'sub-monthly-salary',
      note: '薪資',
      tags: '',
    },
    {
      id: 'tx-salary-2',
      book_id: 'book-family',
      date: '2026-05-03',
      amount_minor: '12500000',
      currency: 'TWD',
      kind: 'income',
      category_id: 'cat-salary',
      subcategory_id: 'sub-monthly-salary',
      note: '公司薪資',
      tags: '',
    },
    {
      id: 'tx-lawson',
      book_id: 'book-family',
      date: '2026-05-03',
      amount_minor: '100000', // 1000 JPY → 214 TWD when FX-converted
      currency: 'JPY',
      kind: 'expense',
      category_id: 'cat-food',
      subcategory_id: 'sub-breakfast',
      note: 'Lawson',
      tags: '',
    },
    {
      id: 'tx-rice',
      book_id: 'book-family',
      date: '2026-05-03',
      amount_minor: '25000',
      currency: 'TWD',
      kind: 'expense',
      category_id: 'cat-food',
      subcategory_id: 'sub-breakfast',
      note: '米果',
      tags: '',
    },
  ];
  for (const t of sampleTxs) {
    pushRow(tabs, 'transactions', {
      ...t,
      project: '',
      recurring_id: '',
      rev: '1',
      created_at: now,
      updated_at: now,
      deleted_at: '',
    });
  }

  // ─── Meta: schema_version=3, primary_currency=TWD ───
  pushRow(tabs, 'meta', { key: 'schema_version', value: '3' });
  pushRow(tabs, 'meta', { key: 'primary_currency', value: 'TWD' });

  return {
    id: DEV_SPREADSHEET_ID,
    // Must match SPREADSHEET_NAME so bootstrap's filter accepts it as the
    // "existing" workbook and skips the create + verify-and-repair path.
    title: SPREADSHEET_NAME,
    tabs,
  };
}

function pushRow(
  tabs: Map<string, Tab>,
  tabName: TabName,
  values: Record<string, string>,
): void {
  const tab = tabs.get(tabName);
  if (!tab) throw new Error(`Tab not found: ${tabName}`);
  const headers = TABS[tabName].headers;
  const row = headers.map((h) => values[h] ?? '');
  tab.rows.push(row);
}

interface ParsedRange {
  tabName: string;
  startRow: number; // 1-indexed
  endRow: number | null;
}

function parseRange(range: string): ParsedRange {
  // Forms accepted:
  //   `tabName!A1`              — single cell anchor (used by valuesAppend)
  //   `tabName!A1:Z`            — open-ended end row
  //   `tabName!A2:O5`           — bounded
  //   `tabName!A1:Z1`           — single row
  const m = /^(.+?)!([A-Z]+)(\d+)(?::([A-Z]+)(\d+)?)?$/.exec(range);
  if (!m) throw new Error(`Cannot parse range: ${range}`);
  const tabName = m[1]!;
  const startRow = Number.parseInt(m[3]!, 10);
  const endRow = m[5] ? Number.parseInt(m[5]!, 10) : null;
  return { tabName, startRow, endRow };
}

function getRangeRows(tab: Tab, parsed: ParsedRange): string[][] {
  const startIdx = parsed.startRow - 1;
  const endIdx = parsed.endRow == null ? tab.rows.length : parsed.endRow;
  return tab.rows.slice(startIdx, endIdx);
}

export function createMemorySheetsClient(): SheetsClient {
  return {
    async valuesGet(_spreadsheetId, range): Promise<ValueRange> {
      const wb = ensureStore();
      const parsed = parseRange(range);
      const tab = wb.tabs.get(parsed.tabName);
      if (!tab) return { range, values: [] };
      return { range, values: getRangeRows(tab, parsed) };
    },

    async valuesBatchGet(_spreadsheetId, ranges): Promise<BatchGetResponse> {
      const wb = ensureStore();
      const valueRanges = ranges.map((range) => {
        const parsed = parseRange(range);
        const tab = wb.tabs.get(parsed.tabName);
        if (!tab) return { range, values: [] };
        return { range, values: getRangeRows(tab, parsed) };
      });
      return { valueRanges };
    },

    async valuesAppend(_spreadsheetId, range, values) {
      const wb = ensureStore();
      const parsed = parseRange(range);
      const tab = wb.tabs.get(parsed.tabName);
      if (!tab) throw new Error(`Tab not found: ${parsed.tabName}`);
      const startRow = tab.rows.length + 1;
      for (const v of values) tab.rows.push([...v]);
      const endRow = startRow + values.length - 1;
      return { updates: { updatedRange: `${parsed.tabName}!A${startRow}:Z${endRow}` } };
    },

    async valuesUpdate(_spreadsheetId, range, values) {
      const wb = ensureStore();
      const parsed = parseRange(range);
      const tab = wb.tabs.get(parsed.tabName);
      if (!tab) throw new Error(`Tab not found: ${parsed.tabName}`);
      const startIdx = parsed.startRow - 1;
      for (let i = 0; i < values.length; i++) {
        const target = startIdx + i;
        // Pad if needed.
        while (tab.rows.length <= target) tab.rows.push([]);
        tab.rows[target] = [...values[i]!];
      }
      return { range, values };
    },

    async valuesBatchUpdate(spreadsheetId, body: BatchUpdateRequest) {
      // Mochia's repos use single-range valuesUpdate calls; batch is unused
      // but we implement minimally for safety.
      const replies: Array<Record<string, unknown>> = [];
      for (const req of body.requests) {
        const r = req as { range?: string; values?: string[][] };
        if (r.range && r.values) {
          await this.valuesUpdate(spreadsheetId, r.range, r.values);
          replies.push({});
        }
      }
      return { spreadsheetId, replies };
    },

    async spreadsheetsBatchUpdate(_spreadsheetId, _body) {
      // Memory mode never adds/removes sheets — all 6 tabs are pre-created.
      return { spreadsheetId: DEV_SPREADSHEET_ID, replies: [] };
    },

    async spreadsheetsCreate(_props: SpreadsheetCreateProps): Promise<SpreadsheetCreateResponse> {
      const wb = ensureStore();
      return {
        spreadsheetId: wb.id,
        properties: { title: wb.title },
        sheets: [...wb.tabs.values()].map((t) => ({
          properties: { sheetId: 0, title: t.name },
        })),
      };
    },

    async driveFilesList(_query): Promise<DriveFilesListResponse> {
      const wb = ensureStore();
      return {
        files: [
          {
            id: wb.id,
            name: wb.title,
            mimeType: 'application/vnd.google-apps.spreadsheet',
          },
        ],
      };
    },
  };
}
